import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Reveal from "../components/primitives/Reveal";
import SeoHead from "../components/seo/SeoHead";
import LivingImage from "../components/editorial/LivingImage";
import { articleLd } from "../components/seo/structuredData";
import { IMAGES } from "../content/images";

const API = process.env.REACT_APP_BACKEND_URL;

const LetterCover = ({ letter }) => (
  <Section tone="light" className="pt-44 md:pt-56 pb-16" data-testid="letter-cover">
    <Container>
      <Reveal className="max-w-3xl mx-auto text-center flex flex-col items-center">
        <span
          className="font-sans uppercase text-gold"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          Letter No. {letter.number}
        </span>
        <h1
          className="mt-8 font-serif font-normal text-navy text-balance"
          style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1.05, letterSpacing: "-0.01em" }}
          data-testid="letter-title"
        >
          {letter.title}
        </h1>
        <p
          className="mt-6 font-serif italic text-navy/60"
          style={{ fontSize: "18px" }}
        >
          {letter.season || letter.dateline}
        </p>
        <p
          className="mt-5 font-sans uppercase text-navy/55"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          By Quantum US Capital Partners LLC
        </p>
        <span aria-hidden="true" className="mt-8 block h-px w-8 bg-gold" />
      </Reveal>
    </Container>
  </Section>
);

const LetterBody = ({ body }) => {
  // Find the first 'p' block — it gets the drop cap.
  const firstParaIndex = body.findIndex((b) => b.type === "p");

  return (
    <Section tone="light" className="pt-8 pb-24 md:pb-32" data-testid="letter-body">
      <Container>
        <article className="mx-auto" style={{ maxWidth: "65ch" }}>
          {body.map((block, i) => {
            if (block.type === "pullquote") {
              return (
                <Reveal key={i}>
                  <figure
                    className="my-14 md:my-20 flex flex-col items-center text-center"
                    data-testid="letter-pullquote"
                  >
                    <span aria-hidden="true" className="block h-px w-8 bg-gold mb-8" />
                    <blockquote
                      className="font-serif italic text-navy text-balance"
                      style={{
                        fontSize: "clamp(24px, 2.6vw, 36px)",
                        lineHeight: 1.25,
                        opacity: 0.92,
                      }}
                    >
                      &ldquo;{block.text}&rdquo;
                    </blockquote>
                    <span aria-hidden="true" className="block h-px w-8 bg-gold mt-8" />
                  </figure>
                </Reveal>
              );
            }
            const isFirst = i === firstParaIndex;
            const text = block.text;

            if (isFirst && text.length > 1) {
              const firstChar = text[0];
              const rest = text.slice(1);
              return (
                <Reveal key={i}>
                  <p
                    className="font-sans text-navy/85"
                    style={{
                      fontSize: "17px",
                      lineHeight: 1.65,
                      marginBottom: "1.4em",
                    }}
                  >
                    <span
                      className="float-left font-serif text-navy mr-3"
                      style={{
                        fontSize: "80px",
                        lineHeight: "0.85",
                        paddingTop: "0.06em",
                        paddingRight: "0.06em",
                      }}
                      aria-hidden="true"
                    >
                      {firstChar}
                    </span>
                    <span className="sr-only">{firstChar}</span>
                    {rest}
                  </p>
                </Reveal>
              );
            }

            return (
              <Reveal key={i}>
                <p
                  className="font-sans text-navy/85"
                  style={{
                    fontSize: "17px",
                    lineHeight: 1.65,
                    marginBottom: "1.4em",
                  }}
                >
                  {text}
                </p>
              </Reveal>
            );
          })}

          {/* Closing signature */}
          <Reveal>
            <div className="mt-16 flex flex-col items-center text-center">
              <span aria-hidden="true" className="block h-px w-12 bg-gold" />
              <p className="mt-8 font-serif italic text-navy/85" style={{ fontSize: "20px" }}>
                — Quantum US Capital Partners LLC
              </p>
            </div>
          </Reveal>
        </article>
      </Container>
    </Section>
  );
};

const FurtherReading = ({ others }) => (
  <Section tone="offwhite" className="py-20 md:py-28" data-testid="further-reading">
    <Container>
      <Reveal className="max-w-3xl">
        <span
          className="font-sans uppercase text-gold"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          Further Reading
        </span>
        <h2
          className="mt-6 font-serif font-normal text-navy text-balance"
          style={{ fontSize: "clamp(36px, 4.6vw, 56px)", lineHeight: 1.08, letterSpacing: "-0.01em" }}
        >
          Other Letters
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14 max-w-5xl">
        {others.slice(0, 3).map((o, i) => (
          <Reveal key={o.slug} delay={i * 80}>
            <Link
              to={`/letters/${o.slug}`}
              data-testid={`further-${o.slug}`}
              className="group block"
            >
              <span aria-hidden="true" className="block h-px w-10 bg-gold" />
              <span
                className="mt-5 inline-block font-sans uppercase text-gold"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              >
                Letter No. {o.number}
              </span>
              <h3
                className="mt-3 font-serif font-normal text-navy group-hover:text-gold transition-colors duration-300 text-balance"
                style={{ fontSize: "clamp(22px, 2.6vw, 32px)", lineHeight: 1.15 }}
              >
                {o.title}
              </h3>
              <p className="mt-3 font-sans uppercase text-navy/55"
                 style={{ fontSize: "11px", letterSpacing: "0.18em" }}>
                {o.season || o.dateline}
              </p>
              <span
                className="mt-6 inline-flex items-center gap-3 font-sans uppercase text-navy/85 group-hover:text-gold transition-colors duration-300"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              >
                <span>Continue</span>
                <span
                  aria-hidden="true"
                  className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);

const Letter = () => {
  const { slug } = useParams();
  const [letter, setLetter] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abort = false;
    setLetter(null);
    setError(null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!abort) setLetter(data);
      })
      .catch((e) => {
        if (!abort) setError(String(e));
      });

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights`)
      .then((r) => r.json())
      .then((all) => {
        if (!abort && Array.isArray(all)) setOthers(all.filter((x) => x.slug !== slug));
      })
      .catch(() => {});
    return () => {
      abort = true;
    };
  }, [slug]);

  if (error) {
    return (
      <PageShell>
        <Section tone="light" className="pt-44 pb-32">
          <Container>
            <div className="max-w-2xl mx-auto text-center">
              <h1 className="font-serif text-4xl text-navy">Letter not found.</h1>
              <Link to="/insights" className="mt-8 inline-block font-sans uppercase text-navy/85 hover:text-gold" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
                Return to Quantum Letters
              </Link>
            </div>
          </Container>
        </Section>
      </PageShell>
    );
  }

  if (!letter) {
    return (
      <PageShell>
        <Section tone="light" className="pt-44 pb-32">
          <Container>
            <p className="text-center font-sans text-navy/55 text-sm">Loading…</p>
          </Container>
        </Section>
      </PageShell>
    );
  }

  const heroImage = IMAGES[letter.image_key] || IMAGES.insightsStill;

  return (
    <PageShell>
      <SeoHead
        title={`${letter.title} · Quantum Letters`}
        description={letter.excerpt}
        path={`/letters/${letter.slug}`}
        ogImage={heroImage}
        ogType="article"
        structuredData={articleLd({
          slug: letter.slug,
          title: letter.title,
          dateline: letter.dateline,
          excerpt: letter.excerpt,
          image: heroImage,
        })}
      />
      <LetterCover letter={letter} />

      {/* Full-bleed hero image */}
      <div className="relative w-full" data-testid="letter-hero-image">
        <div className="relative w-full h-[60vh] sm:h-[68vh] lg:h-[72vh]">
          <LivingImage
            src={heroImage}
            alt={letter.title}
            className="absolute inset-0"
            variant="editorial"
            eager
            overlayStyle={{
              background:
                "linear-gradient(180deg, rgba(11,19,43,0.15) 0%, rgba(11,19,43,0.0) 35%, rgba(11,19,43,0.0) 65%, rgba(11,19,43,0.3) 100%)",
            }}
          />
        </div>
      </div>

      <LetterBody body={letter.body} />

      <FurtherReading others={others} />
    </PageShell>
  );
};

export default Letter;
